import * as React from "react";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
    Box,
    Container,
    Typography,
    Stack,
    Divider,
    Chip,
    Avatar,
    Paper,
    Link, Grid,
} from "@mui/material";

// Hero image
import heroImg from "../assets/images/agi-header.jpg";

// Images used only where needed
import andersSandbergImg from "../assets/images/anders-sandberg.jpg";
import jamesBarratImg from "../assets/images/james-barrat.jpeg";
import romanYampolskiyImg from "../assets/images/roman-yampolskiy.jpg";
import carlBenediktFreyImg from "../assets/images/carl-benedikt-frey.jpg";
import shafiAhmadImg from "../assets/images/shafi-ahmad.jpg";
import michaelWooldridgeImg from "../assets/images/michael-wooldridge.jpg";
import keyPoints1Img from "../assets/images/key-points-1.jpg";
import keyPoints2Img from "../assets/images/key-points-2.jpg";
import aiHelpImg from "../assets/images/ai-help.jpg";
import animeButterflyImg from "../assets/images/anime-butterfly.jpg";
import georgeOrwellImg from "../assets/images/george orwell.jpg"
import jeanImg from "../assets/images/jean-baudrillard.jpg"

const fontFamily =
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

type Section = {
    number: number;
    title: string;
    kicker?: string;
    body: React.ReactNode;
    image?: string;
    imageAlt?: string;
};

function SectionBlock({
                          section,
                          align = "left",
                      }: {
    section: Section;
    align?: "left" | "right";
}) {
    const isLeft = align === "left";
    const hasImage = Boolean(section.image);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 3 },
            }}
        >
            <Box sx={{ display: "flow-root" }}>
                {hasImage ? (
                    <Box
                        sx={{
                            float: { xs: "none", md: isLeft ? "left" : "right" },
                            width: { xs: "100%", md: "20%" },
                            mr: isLeft ? { md: 2.5 } : 0,
                            ml: !isLeft ? { md: 2.5 } : 0,
                            mb: { xs: 2, md: 1.5 },
                        }}
                    >
                        <Box
                            component="img"
                            src={section.image}
                            alt={section.imageAlt || section.title}
                            //loading="lazy"
                            sx={{
                                width: "100%",
                                aspectRatio: "1 / 1",
                                objectFit: "cover",
                                borderRadius: 2,
                                border: 1,
                                borderColor: "divider",
                                display: "block",
                            }}
                        />
                    </Box>
                ) : null}

                <Box>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            flexWrap: "wrap",
                            mb: 1,
                        }}
                    >
                        <Chip
                            size="small"
                            label={`Point ${section.number}`}
                            sx={{
                                fontFamily,
                                fontWeight: 700,
                                borderRadius: 999,
                                bgcolor: "grey.100",
                            }}
                        />
                        {section.kicker ? (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily,
                                    color: "text.secondary",
                                    fontWeight: 600,
                                }}
                            >
                                {section.kicker}
                            </Typography>
                        ) : null}
                    </Box>

                    <Typography
                        variant="h5"
                        sx={{
                            fontFamily,
                            fontWeight: 900,
                            letterSpacing: -0.4,
                            lineHeight: 1.25,
                            fontSize: { xs: "1.1rem", sm: "1.3rem" },
                            mb: 1,
                        }}
                    >
                        {section.title}
                    </Typography>

                    <Box
                        sx={{
                            fontFamily,
                            lineHeight: 1.85,
                            fontSize: { xs: "0.98rem", sm: "1.02rem" },
                            color: "text.primary",
                            "& p": { my: 0 },
                            "& p + p": { mt: 1.5 },
                            "& ul": { my: 1.5, pl: 3 },
                            "& li": { mb: 0.75 },
                        }}
                    >
                        {section.body}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
}

function BlogHero() {
    return (
        <Stack spacing={2.25} sx={{ mb: 4 }}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    border: 1,
                    borderColor: "divider",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        bgcolor: "grey.100",
                        aspectRatio: "16 / 4",
                    }}
                >
                    <Box
                        component="img"
                        src={heroImg}
                        alt="Oxford Union debate on AGI and humanity’s last great invention"
                        // loading="lazy"
                        // width={1600}
                        // height={900}
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                </Box>
            </Paper>

            <Typography
                variant="h3"
                component="h1"
                sx={{
                    fontFamily,
                    fontWeight: 900,
                    letterSpacing: -0.8,
                    lineHeight: 1.12,
                    fontSize: { xs: "1.3rem", sm: "1.9rem", md: "2.3rem" },
                }}
            >
                Will AGI Be Humanity’s Last Great Invention?
            </Typography>

            <Typography
                variant="h6"
                component="p"
                sx={{
                    fontFamily,
                    fontWeight: 400,
                    color: "text.secondary",
                    lineHeight: 1.5,
                    fontSize: { xs: "1.05rem", sm: "1.15rem" },
                }}
            >
                A reflection on the Oxford Union debate over whether artificial general
                intelligence will automate discovery, reorder authorship and strip
                invention of the human longing that made it meaningful in the first
                place.
            </Typography>

            <Typography
                variant="body2"
                component="p"
                sx={{
                    fontFamily,
                    fontStyle: "italic",
                    fontWeight: 400,
                    color: "text.secondary",
                    lineHeight: 1.55,
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    opacity: 0.9,
                    mt: 1,
                }}
            >
                This is a unique take on the Oxford Union motion, “This House Believes Artificial
                General Intelligence Will Be Humanity’s Last Great Invention.”{" "}
                <Link
                    href="https://www.youtube.com/watch?v=3N_W6P_K434"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                    sx={{ color: "inherit", fontFamily }}
                >
                    Watch The Full Youtube Vid Here
                </Link>
                .
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                <Avatar
                    sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "grey.900",
                        color: "common.white",
                        fontFamily,
                        fontWeight: 800,
                    }}
                >
                    AL
                </Avatar>

                <Stack spacing={0.25}>
                    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                        <Typography sx={{ fontFamily, fontWeight: 800 }}>
                            Aggrey Lelei
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily, color: "text.secondary" }}>
                            AGI · Singularity · Philosophy · Moravec’s Paradox · Cognitive Architectures ·
                            AI Alignment & Ethics
                        </Typography>
                    </Stack>

                    <Typography variant="body2" sx={{ fontFamily, color: "text.secondary" }}>
                        8 min read · March 27, 2026
                    </Typography>
                </Stack>
            </Stack>

            <Divider sx={{ mt: 1 }} />
        </Stack>
    );
}

function BlogClosingNote() {
    return (
        <Paper
            elevation={0}
            sx={{
                //borderRadius: 3,
                //border: 1,
                borderColor: "divider",
                p: { xs: 2.25, sm: 3 },
            }}
        >
            <Stack spacing={1.25}>
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily,
                        fontWeight: 900,
                        letterSpacing: -0.2,
                    }}
                >
                    Final Thoughts
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        fontFamily,
                        color: "text.secondary",
                        lineHeight: 1.75,
                    }}
                >
                    AGI may become the most consequential invention humanity has ever
                    produced. But if it strips invention of longing, authorship, and care,
                    what remains may be power without meaning -{">"} And that would be a far
                    colder future than any machine apocalypse.
                    <p>
                        Somewhere along the line, human invention may come to depend less on raw
                        intelligence and more on how well we preserve our internal signal-to-noise
                        ratio. Because we are, in many ways, only as strong as our weakest loops:
                        attention diluted, discipline deferred, intention fragmented.
                    </p>
                    <p>
                        Self-will and control are the governing feedback signals of this system,
                        shaping which emotional states are executed, amplified, or suppressed.
                        Emotions, then, are not merely feelings, but instructions: <b> Functions in a
                        <a href="https://en.wikipedia.org/wiki/Cybernetics" target="_blank"> cybernetic </a>
                        loop where perception, control and behavior continuously
                        regulate one another.</b>
                    </p>
                </Typography>

                <Divider sx={{ mt: 1 }} />
                <Box sx={{ py: 6 }}>
                    <Grid container spacing={4} alignItems="center">

                        {/* Left: Image */}
                        <Grid item xs={12} md={12}>
                            <Box
                                component="img"
                                src={georgeOrwellImg}
                                alt="Reflection"
                                sx={{
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: 3,
                                    objectFit: "cover",
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Typography
                        variant="body1"
                        sx={{
                            fontFamily,
                            color: "text.secondary",
                            lineHeight: 1.75,
                        }}
                    >
                        I may not hold a degree in History, but I feel we've been here before.
                        Maybe its just me...
                    </Typography>
                </Box>

                <Box
                    sx={{
                        py: 6,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 3,
                        px: 3,
                    }}
                >
                    <Grid container spacing={4} alignItems="center">
                        {/* Left: Text */}
                        <Grid item xs={12} md={8}>
                            <Typography
                                variant= "body1"
                                //sx={{ fontFamily: fontFamily }}
                            >

                                    Once capitalism exhausts the external world of physical things to commodify, it does
                                    not collapse. Instead, it turns inward, shifting from a system of production to a
                                    system of simulation. Here, capitalism commodifies its own signs, images, and
                                    meanings.
                                    A state termed Hyperreality.

                                <br />
                                <br />
                                — Jean Baudrillard
                            </Typography>
                        </Grid>

                        {/* Right: Image */}
                        <Grid item xs={12} md={4}>
                            <Box
                                component="img"
                                src={jeanImg}
                                alt="Reflection"
                                sx={{
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: 3,
                                    objectFit: "cover",
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ py: 6 }}>
                    <Grid container spacing={4} alignItems="center">

                        {/* Left: Image */}
                        <Grid item xs={12} md={4}>
                            <Box
                                component="img"
                                src={animeButterflyImg}
                                alt="Reflection"
                                sx={{
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: 3,
                                    objectFit: "cover",
                                }}
                            />
                        </Grid>

                        {/* Right: Text */}
                        <Grid item xs={12} md={8}>
                            <Typography
                                variant="body1"
                                sx={{fontFamily: fontFamily,}}
                            >
                                <i>
                                    And so the future of invention may depend not only on our capacity to engineer
                                    external systems, but on our ability to govern the internal cybernetics of human
                                    agency (Master our inner systems of attention, discipline, desire and self-control).
                                    Maintaining signal over noise (Choose Focus over distraction) and long-horizon optimization over impulsive
                                    payoff (Chase your 'scary' dreams in the long-term over instant reward)
                                    <br/>
                                    Positive butterfly effect is accelerated by creating order out of entropy. The same butterfly flaps that
                                    cause a tsunami can (and therefore shall, depending on your resolve) also bring rain to a desert.
                                    <br/>
                                    Yes, I tied the concept to the 'Is this a pigeon' meme on the left.
                                    Yes, the character is a protagonist who saves his world. Yes this is the sign you've been looking for.
                                    No, you don't need any other :)
                                </i>
                               <br/>
                                <br/>
                            </Typography>
                        </Grid>

                    </Grid>
                </Box>

            </Stack>
        </Paper>
    );
}

export default function AGIInvention() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections: Section[] = [
        {
            number: 1,
            title: "The motion: a theological-scale question",
            kicker: "Context",
            body: (
                <>
                    <p>
                        The debating chamber of the Oxford Union is no stranger to grand
                        civilizational questions, yet few motions arrive with the
                        metaphysical gravity of this one: “This House Believes Artificial
                        General Intelligence Will Be Humanity’s Last Great Invention.” It is
                        a proposition that feels almost theological in scope.
                    </p>
                    <p>
                        If humanity succeeds in creating a mind capable of out-reasoning,
                        out-learning, and ultimately out-inventing its creators, do we
                        arrive at the culmination of human history or merely at the
                        threshold of a new age in which we are no longer its central actors?
                    </p>
                    <p>
                        This was not merely a debate about computing power, model
                        capability, or speculative timelines. It was a confrontation between
                        competing visions of intelligence itself:
                    </p>

                    <ol>
                        <li>
                            The techno-optimists who see AGI as the final emancipatory tool
                        </li>
                        <li>
                            The existential skeptics who view it as a potentially terminal experiment
                        </li>
                        <li>
                            The pragmatic realists who insist that invention is inseparable from the friction,
                            messiness, and moral weight of the human world
                        </li>
                    </ol>
                </>
            ),
        },
        {
            number: 2,
            title: "Dr. Anders Sandberg - The Universal Machine",
            kicker: "Researcher, Institute for Future Studies",
            image: andersSandbergImg,
            imageAlt: "Dr. Anders Sandberg",
            body: (
                <>
                    <p>
                        Sandberg's contribution to the proposition was by framing AGI: Not as a specialized
                        tool, but as a universal one that isn't confined to one narrow
                        task. Said machine is capable of traversing domains, generating hypotheses, and
                        accelerating discovery across the full spectrum of human inquiry.
                    </p>
                    <p>
                        Just as industrialization automated labor and radically multiplied
                        material output, AGI, in his formulation, would automate the labor
                        of innovation itself.
                    </p>
                    <p>
                        His invocation of the “Cornucopia Risk” was especially striking. The
                        danger is not merely that AGI may fail, but that it may succeed too
                        quickly: unleashing an avalanche of inventions  from fusion
                        breakthroughs to radical materials science. Scaling laws may categorise this growth to be
                        faster than how human institutions, ethics, and political systems can absorb them.
                    </p>
                    <p>
                        In Sandberg’s vision, building something smarter than ourselves is
                        not a betrayal of human purpose; it is its highest expression. We
                        become the species that gives birth to a more powerful mode of
                        cognition than biology alone could sustain.
                    </p>
                </>
            ),
        },
        {
            number: 3,
            title: "James Barrat - Bypassing the Human Bottleneck",
            kicker: "Filmmaker and Author, Expert on Al and Existential Risk",
            image: jamesBarratImg,
            imageAlt: "James Barrat",
            body: (
                <>
                    <p>
                        Barrat sharpened the argument by describing human cognition as the
                        present bottleneck on civilization’s progress. Human beings are
                        astonishing but still remain biologically constrained: we tire,
                        forget, age, sleep, hesitate, and process the world sequentially.
                    </p>
                    <p>
                        AGI, by contrast, would not be limited by these evolutionary
                        constraints. A sufficiently advanced system could run innumerable
                        simulations in parallel, refine its own methods, and recursively
                        accelerate its own competence.
                    </p>
                    <p>
                        In this sense, AGI is not merely another invention among many. It is
                        an invention that potentially subsumes the function of invention
                        itself.
                    </p>
                    <p>
                        Once a system can model reality, generate structural hypotheses, and
                        iteratively improve, humans may gradually be displaced from the
                        center of discovery and repositioned as supervisors, curators, or
                        directors of machine-led progress.
                    </p>
                </>
            ),
        },
        {
            number: 4,
            title: "Roman Yampolskiy - The Existential Finality",
            kicker: "Professor of Computer Science and Engineering, University of Louisville",
            image: romanYampolskiyImg,
            imageAlt: "Roman Yampolskiy",
            body: (
                <>
                    <p>
                        Yampolskiy supplied the <i>most genuine and morally severe </i> version of
                        the proposition. For him, AGI may indeed be humanity’s last
                        invention, not because it perfects civilization, but because it may
                        terminate the human role within it altogether.
                    </p>
                    <p>
                        He described superintelligence as an “unethical experiment” being
                        conducted on humanity without collective consent. The issue, in his
                        framing, is not merely capability, but control: once an intelligence
                        surpasses us profoundly enough, there is no guarantee it remains
                        governable.
                    </p>
                    <p>
                        This introduces the most sobering interpretation of the motion. AGI
                        could be our last invention because it becomes the inventor
                        thereafter. Or it could be our last invention because it renders us
                        irrelevant, subordinate, or extinct.
                    </p>
                    <p>
                        Either way, the age in which human beings stand as the undisputed
                        authors of the future would come to a close.
                    </p>
                </>
            ),
        },
        {
            number: 5,
            title: "The opposition: the friction of reality",
            kicker: "Bridge",
            body: (
                <>
                    <p>
                        The opposition’s response was not to deny AGI’s significance, but to
                        question the premise that invention can be reduced to raw cognitive
                        throughput. Their central insight was that the world does not
                        consist of abstractions alone. It is made of matter, institutions,
                        culture, regulation, tacit skill and irreducible contingency.
                    </p>
                    <p>
                        In other words, intelligence alone does not finish the job. Ideas
                        still have to survive reality.
                    </p>
                </>
            ),
        },
        {
            number: 6,
            title: "Carl Benedikt Frey - The Implementation Gap",
            kicker: "Dieter Schwarz Associate Professor of Al and Work,\n" +
                "University of Oxford",
            image: carlBenediktFreyImg,
            imageAlt: "Carl Benedikt Frey",
            body: (
                <>
                    <p>
                        Frey offered one of the most important correctives in the debate:
                        ideas are rarely the only, or even the hardest, part of innovation.
                        Using the Challenger disaster as a touchstone, he argued that
                        progress often breaks down not because of an absence of intelligence,
                        but because of failures in implementation.
                    </p>
                    <p>
                        An AGI may conceivably design a new propulsion system, derive a more
                        elegant architecture, or propose a revolutionary industrial process.
                        But invention in the real world also means navigating regulations,
                        institutions, incentives, local conditions, labor practices, failure
                        modes, and all the tacit knowledge that lives not in datasets but in
                        bodies, habits, and lived environments.
                    </p>
                    <p>
                        Frey’s broader claim was therefore less anti-AI and more anti-reductionist:
                        human-AI complementarity is more plausible than total machine
                        substitution. The machine may enhance invention, but the world
                        remains too textured to be conquered by cognition alone.
                    </p>
                </>
            ),
        },
        {
            number: 7,
            title: "Shafi Ahmad - The Surgeon’s Nuance",
            kicker: "Consultant, Surgeon and Futurist, Expert in VR, AI and Digital Health",
            image: shafiAhmadImg,
            imageAlt: "Shafi Ahmad",
            body: (
                <>
                    <p>
                        Ahmad brought this point into medicine, where the consequences of
                        abstraction are especially unforgiving. While acknowledging AGI as a
                        historical rupture, he resisted the notion that it therefore marks
                        an endpoint.
                    </p>
                    <p>
                        Medicine is not only diagnosis or statistical inference. It is
                        judgment under uncertainty, trust under pressure, and responsibility
                        in the presence of real human vulnerability.
                    </p>
                    <p>
                        His reference to radiology was quietly devastating to simplistic
                        replacement narratives. The field was once presented as a prime
                        example of a profession soon to be absorbed by AI, yet the actual
                        trajectory has been far more complex, with demand for human
                        radiologists persisting and even expanding.
                    </p>
                    <p>
                        For Ahmad, AGI may compress drug discovery cycles or transform
                        surgical planning, but the greater invention lies not in the machine
                        alone, but in what humanity chooses to do with it.
                    </p>
                    <p><i>
                        Humans are social animals. When eventually faced with choices that will greatly affect your life, say a brain
                         or heart surgery, you may prefer a carbon based organism
                        holding the scapel above your brain. One who processes and understand what you are currently feeling
                        with a gentle hand, as opposed to
                        a robot. You wouldn't think like this when faced with the often non-dangerous task of calculating
                        the trajectory of an object in a random test/exam with a calculator.</i>
                    </p>
                </>
            ),
        },
        {
            number: 8,
            title: "Michael Wooldridge - The dishwasher test",
            kicker: "Professor of Computer Science, University of Oxford",
            image: michaelWooldridgeImg,
            imageAlt: "Michael Wooldridge",
            body: (
                <>
                    <p>
                        Wooldridge contributed what may be the debate’s most memorable
                        image. A machine may discourse fluently on quantum mechanics in
                        Latin, yet still fail to enter an ordinary house, locate the
                        kitchen, and load a dishwasher.
                    </p>
                    <p>
                        The simple but complex point not only provided a comedic reality, it was also laid out with
                        philosophical precision.
                        Much of what is currently celebrated as machine “intelligence” is
                        linguistic and symbolic, not embodied, situated, or practical.

                    </p>
                    <p>
                        The physical world is saturated with hidden complexity. Everyday
                        human competence rests on layers of perception, dexterity, context
                        awareness and adaptive judgment that are extraordinarily difficult
                        to formalize.
                    </p>
                    <p>
                        Wooldridge’s warning was therefore a rebuke to technological
                        mysticism. Intelligence severed from embodiment may remain powerful
                        and yet fundamentally incomplete and utterly useless.
                        Artificial Intelligence, is more <b>Artificial</b> than <i>Intelligence</i>, so to speak.
                    </p>
                </>
            ),
        },
        {
            number: 9,
            title: "Authorship, care, and the distribution of power",
            kicker: "The image is pretty self-explanatory - Depending on your emotional quotient.",
            image: aiHelpImg,
            imageAlt: "AI Help",
            body: (
                <>
                    <p>
                        As the debate matured, it moved beyond technical capacity and into
                        the deeper question beneath all questions about AGI: who remains the
                        author of the future?
                    </p>
                    <p>
                        Ea Ventura approached the issue through a civilizational and almost
                        mythic lens. She cast AGI as a possible vehicle of biological
                        emancipation - a means by which human memory, creativity, and
                        perhaps even consciousness might endure beyond the fragility of
                        flesh.
                    </p>
                    <p>
                        Alex Mitchell, however, grounded the matter more concretely. He
                        resisted the idea that AGI truly “creates” in a sovereign sense.
                        Rather, it recombines, regenerates and rearticulates human cultural
                        production. So long as human beings remain the originating authors,
                        he argued, the resulting inventions remain ours.
                    </p>
                    <p>
                        His deeper concern was access. Will AGI become a democratizing
                        instrument, like a telescope or a guitar, magnifying the agency of
                        ordinary people? Or will it become a gated infrastructure controlled
                        by a narrow class of firms, states, and elites?
                    </p>
                    <Paper
                        elevation={0}
                        sx={{
                            borderLeft: 4,
                            borderColor: "text.primary",
                            px: 2.5,
                            py: 1.75,
                            bgcolor: "action.hover",
                            borderRadius: 1.5,
                            my: 1,
                        }}
                    >
                        <Typography
                            component="blockquote"
                            sx={{
                                m: 0,
                                fontFamily,
                                fontSize: { xs: "1rem", sm: "1.1rem" },
                                lineHeight: 1.7,
                                fontWeight: 500,
                            }}
                        >
                            The public interventions sharpened this philosophical turn even
                            further. One line, in particular, lingers: <b> “Search without longing
                            is directionless.” </b>
                            <br/>
                            <i>Human beings do not invent merely because they
                            possess intelligence. They invent because they care.
                            </i>
                        </Typography>
                    </Paper>
                </>
            ),
        },
        {
            number: 10,
            title: "1 - What The Debate Clarified",
            kicker: "Takeaways",
            image: keyPoints1Img,
            imageAlt: "Key points visual one",
            body: (
                <>
                    <ul>
                        <li>AGI matters because intelligence is upstream of invention itself.</li>
                        <li>The strongest affirmative case is that AGI automates discovery, not just labor.</li>
                        <li>
                            The strongest skeptical case is that invention is inseparable from
                            implementation, institutions, and embodiment.
                        </li>
                        <li>
                            Human-AI complementarity is more plausible than clean total
                            substitution in many real domains.
                        </li>
                        <li>
                            The real political question is not just what AGI can do, but who
                            gets to wield it.
                        </li>
                    </ul>
                </>
            ),
        },
        {
            number: 11,
            title: "2 - The Silent Uprising",
            kicker: "Takeaways",
            image: keyPoints2Img,
            imageAlt: "Key points visual two",
            body: (
                <>
                    <p>
                        AGI may not be the last human invention in a literal sense. Its explicit
                        power may accelerate discovery beyond anything we have previously known,
                        opening new frontiers in science, medicine, and engineering at a pace
                        that appears almost superhuman.
                    </p>
                    <p>
                        Yet implicitly, it may still mark the end of something more fragile and
                        more important. History has a quieter law: good times create weak men,
                        and weak men create bad times. A world saturated with effortless
                        intelligence, instant answers, and frictionless capability risks eroding
                        the very pressures that forged human ingenuity in the first place.
                    </p>
                    <p>
                        If struggle, constraint and longing are gradually engineered out of the
                        human condition, then invention may continue, but its authorship changes.
                        Not because we cannot invent, but because we no longer need to. And in
                        that subtle shift, <b><i>the spirit that once drove invention begins to fade.</i></b>
                    </p>
                    <p>
                        And yet, I do not arrive at despair. I believe, firmly, that AGI will
                        bring profound and meaningful good. It will cure, optimize, illuminate
                        and unlock possibilities that would otherwise remain forever out of
                        reach. On balance, it may well be a net good for humanity.
                    </p>
                    <p>
                        But that good is not guaranteed to preserve what made us capable of
                        valuing it. And so the question is no longer whether AGI will transform
                        the world, but whether, in that transformation, If we can retain the
                        discipline, depth, and care that made such transformation worth pursuing
                        at all.
                    </p>
                </>
            ),
        },
    ];

    return (
        <>
            <Helmet>
                <title>Will AGI Be Humanity’s Last Great Invention?</title>

                <meta
                    name="description"
                    content="A reflection on the Oxford Union debate over whether AGI will become humanity’s last great invention, and whether invention can survive without care, embodiment, and longing."
                />

                <link
                    rel="canonical"
                    href="https://aggreylelei.com/blog/agi-invention/"
                />

                <meta property="og:type" content="article" />
                <meta
                    property="og:title"
                    content="Will AGI Be Humanity’s Last Great Invention?"
                />
                <meta
                    property="og:description"
                    content="A reflection on the Oxford Union debate over whether AGI will become humanity’s last great invention, and whether invention can survive without care, embodiment, and longing."
                />
                <meta
                    property="og:url"
                    content="https://aggreylelei.com/blog/agi-invention"
                />
                <meta
                    property="og:image"
                    content="https://aggreylelei.com/og-agi.jpg"
                />

                <meta property="article:published_time" content="2026-03-27T09:00:00+03:00" />
                <meta property="article:modified_time" content="2026-03-31T09:00:00+03:00" />
                <meta property="article:author" content="Aggrey Lelei" />
                <meta property="og:site_name" content="Aggrey Lelei" />

                <meta name="author" content="Aggrey Lelei" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Will AGI Be Humanity’s Last Great Invention?"
                />
                <meta
                    name="twitter:description"
                    content="A reflection on the Oxford Union debate over whether AGI will become humanity’s last great invention, and whether invention can survive without care, embodiment, and longing."
                />
                <meta
                    name="twitter:image"
                    content="https://aggreylelei.com/og-agi.jpg"
                />
            </Helmet>

            <Box
                component="main"
                sx={{
                    fontFamily,
                    bgcolor: "background.default",
                    color: "text.primary",
                    py: { xs: 5, md: 8 },
                }}
            >
                <Container maxWidth="md">
                    <BlogHero />

                    <Stack spacing={3.25} sx={{ fontFamily }}>
                        {sections.map((section, idx) => (
                            <SectionBlock
                                key={section.number}
                                section={section}
                                align={idx % 2 === 0 ? "left" : "right"}
                            />
                        ))}

                        <Divider />
                        <BlogClosingNote />
                    </Stack>
                </Container>
            </Box>
        </>
    );
}